import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Box, Stack } from '@mui/material';

const ItemList = ({ buildItemToDisplay, isReadyToLoad, items, requestApi }) => {
  /**
   * This ItemList will render a list of items to the screen.
   *
   * What makes it unique is that it will delay gathering the items that it needs to render until the
   * parent component says that it is "isReadyToLoad." Once it begins loading information, it will maintain
   * the state internally.
   *
   * Effectively, this is a good component to use in parent components who want to fully load a large set of data, but need
   * data to be *lazy loaded* at a time AFTER the initial render of the component.
   */

  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [neededDocumentCount, setNeededDocumentCount] = useState(1); // Initially, we will assume there is at least one item that we need to go get.

  const itemCount = items.length;

  const stackedItems = items.map((item) => (
    <Box key={item._id} display="flex" alignItems="center" gap="8px">
      {buildItemToDisplay(item)}
    </Box>
  ));

  useEffect(() => {
    const fetchData = () => {
      // Go and get more items.
      setIsLoading(true);
      requestApi(setNeededDocumentCount, setIsLoading, page, items);
    };

    /**
     * We send out another request if all of these conditions are met:
     *
     * 1) We are ready to load (the accordian is open)
     * 2) There is no current active request that is still out there being processed
     * 3) We have not yet retrieved all the data that we need
     */
    if (isReadyToLoad && !isLoading && itemCount < neededDocumentCount) {
      fetchData();
      setPage(page + 1);
    }
  }, [isReadyToLoad, isLoading, items, itemCount, neededDocumentCount, page, requestApi]);

  if (isReadyToLoad) {
    return <Stack gap="16px">{stackedItems}</Stack>;
  }

  return 'Loading...';
};

ItemList.propTypes = {
  buildItemToDisplay: PropTypes.func,
  isReadyToLoad: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  requestApi: PropTypes.func,
};

export default ItemList;
